import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { addDays, dayLabel, dowIndex, longDate, MONTHS } from '../lib/format.ts';
import { Icon } from './Icon.tsx';

const WD = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const ym = (d: string) => d.slice(0, 7);
const monthStart = (m: string) => `${m}-01`;
const addMonths = (m: string, n: number) => {
  const y = Number(m.slice(0, 4)), mo = Number(m.slice(5, 7)) - 1 + n;
  return `${y + Math.floor(mo / 12)}-${String(((mo % 12) + 12) % 12 + 1).padStart(2, '0')}`;
};
const daysIn = (m: string) => new Date(Date.UTC(Number(m.slice(0, 4)), Number(m.slice(5, 7)), 0)).getUTCDate();

// Seletor de dia: um botão com o dia escolhido que abre o calendário do mês, com atalhos (hoje, amanhã, em 1 semana).
// `min`/`max` limitam o que dá para escolher; `closed` marca os dias em que o salão não atende.
export function DateSelect({ label, value, onChange, today, min, max, closed, shortcuts = true }: {
  label: string; value: string; onChange: (d: string) => void; today: string; min?: string; max?: string; closed?: (d: string) => boolean; shortcuts?: boolean;
}) {
  const lo = min ?? today;
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(ym(value));
  const [focus, setFocus] = useState(value);
  const root = useRef<HTMLDivElement>(null);
  const id = useId();
  const ok = (d: string) => d >= lo && (!max || d <= max) && !(closed?.(d));

  useEffect(() => { if (open) { setMonth(ym(value)); setFocus(value); } }, [open, value]);
  useEffect(() => {
    if (!open) return;
    const outside = (e: Event) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', outside);
    document.addEventListener('touchstart', outside);
    return () => { document.removeEventListener('mousedown', outside); document.removeEventListener('touchstart', outside); };
  }, [open]);

  const pick = (d: string) => { if (!ok(d)) return; onChange(d); setOpen(false); };
  const canPrev = monthStart(month) > monthStart(ym(lo));
  const canNext = !max || monthStart(addMonths(month, 1)) <= max;
  const blanks = dowIndex(monthStart(month));
  const cells = Array.from({ length: daysIn(month) }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`);

  const shift = (n: number) => {
    const d = addDays(focus, n);
    if (d < lo || (max && d > max)) return;
    setFocus(d); setMonth(ym(d));
    requestAnimationFrame(() => root.current?.querySelector<HTMLButtonElement>(`[data-d="${d}"]`)?.focus());
  };
  const key = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); (root.current?.querySelector('.dsel-btn') as HTMLElement | null)?.focus(); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); shift(1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); shift(-1); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); shift(7); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); shift(-7); }
  };

  return (
    <div className="dsel" ref={root} onKeyDown={key}>
      <button type="button" className={'dsel-btn' + (open ? ' open' : '')} aria-haspopup="dialog" aria-expanded={open} aria-controls={id} aria-label={`${label}: ${longDate(value)}`} onClick={() => setOpen(!open)}>
        <Icon name="cal" />
        <span>{longDate(value)}</span>
        {(value === today || value === addDays(today, 1)) && <small>{value === today ? 'hoje' : 'amanhã'}</small>}
        <Icon name="chev" />
      </button>
      {open && (
        <div id={id} className="dsel-pop" role="dialog" aria-label={label}>
          {shortcuts && (
            <div className="dsel-quick">
              {[0, 1, 7].map((n) => { const d = addDays(today, n); return ok(d) ? <button key={n} type="button" className={d === value ? 'on' : ''} onClick={() => pick(d)}>{n === 0 ? 'Hoje' : n === 1 ? 'Amanhã' : 'Em 1 semana'}</button> : null; })}
            </div>
          )}
          <div className="dsel-head">
            <button type="button" aria-label="Mês anterior" disabled={!canPrev} onClick={() => setMonth(addMonths(month, -1))}><Icon name="back" /></button>
            <b aria-live="polite">{MONTHS[Number(month.slice(5, 7)) - 1]!.replace(/^./, (c) => c.toUpperCase())} de {month.slice(0, 4)}</b>
            <button type="button" aria-label="Próximo mês" disabled={!canNext} onClick={() => setMonth(addMonths(month, 1))}><Icon name="chev" /></button>
          </div>
          <div className="dsel-grid" role="grid" aria-label={label}>
            {WD.map((w, i) => <span key={i} className="dsel-wd" aria-hidden="true">{w}</span>)}
            {Array.from({ length: blanks }, (_, i) => <span key={'b' + i} />)}
            {cells.map((d) => {
              const can = ok(d);
              return (
                <button key={d} type="button" data-d={d} role="gridcell" tabIndex={d === focus ? 0 : -1} disabled={!can}
                  className={(d === value ? 'sel' : '') + (d === today ? ' today' : '') + (closed?.(d) && d >= lo ? ' off' : '')}
                  aria-label={`${longDate(d)}${d === today ? ', hoje' : ''}${closed?.(d) ? ', fechado' : ''}`} aria-selected={d === value} onClick={() => pick(d)}>
                  {Number(d.slice(8))}
                </button>
              );
            })}
          </div>
          {closed && <p className="dsel-note">Dias riscados: o salão não atende.</p>}
        </div>
      )}
    </div>
  );
}
